import { CoogoStatusMark } from '@/components/coogo-status-mark'
import { ASSISTANT_ICON_BG, ASSISTANT_ICON_BORDER, INK_200, MUTED_TEXT, OK } from './chat-tokens'
import { useStudioStore } from '@/store/studio-store'
import { useStudioConversationSurfaceLabel } from './artifact-mode-switcher'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import { ProjectConversationScopeBar } from '@/features/chat/components/conversation-scope-bar'

/**
 * Cogo conversation header — concept2 brand presence above the message list.
 *
 * Source: `docs/studio/design-concept2/src/chat.jsx:73-97`. Shows the
 * cogo lockup (icon + name + Online · context) so the rail reads as a
 * dedicated user/agent work loop rather than an unlabelled message list.
 *
 * Deliberate deviation from concept2: no Clear / Close buttons. concept2
 * is a user-toggled floating dock with persistent message history; the
 * cogo-studio rail is dispatch-driven (visibility tied to
 * `dispatchedOutput`). Termination flows through the `MissView` retry
 * button or `clearDispatch`. Adding Clear/Close here would either
 * duplicate that flow or create awkward partial-clear states.
 */
export function ChatHeader() {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const surfaceLabel = useStudioConversationSurfaceLabel(activeRailItem)

  return (
    <div data-testid="chat-header" className="flex-shrink-0" style={{ borderBottom: `1px solid ${INK_200}` }}>
      <div className="flex items-center gap-2.5 px-4 py-3.5">
        <div
          className="flex items-center justify-center"
          data-testid="chat-header-avatar"
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            background: ASSISTANT_ICON_BG,
            border: `1px solid ${ASSISTANT_ICON_BORDER}`,
          }}
        >
          <CoogoStatusMark mode="brand" state="idle" pace="held" size="inline-compact" />
        </div>
        <div className="flex flex-col">
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: 0,
              lineHeight: 1.2,
            }}
          >
            coogo
          </div>
          <div
            className="flex items-center gap-1.5"
            style={{
              fontSize: 11,
              color: MUTED_TEXT,
              lineHeight: 1.3,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: OK,
                flexShrink: 0,
              }}
            />
            {copy.conversation(surfaceLabel)}
          </div>
        </div>
      </div>
      <ProjectConversationScopeBar className="mx-3 mb-3" />
    </div>
  );
}
